import { Box, Button, Flex, Tag, Text, Textarea } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';

import { AgentV } from '@finley-technologies/finley-types';

import { useAgentVAttachments } from './useAgentVAttachments';
import { useAgentVStream, AgentVTurn } from './useAgentVStream';
import { Icon } from 'packages/common-frontend/components/icons';
import {
  FinleyThinkingSpinner,
  PromptChatBubble,
} from '~/packages/finleyAi/ui/FinleyAiThreadComponents';

const BlockView = ({ block }: { block: AgentV.AgentVBlock }) => {
  switch (block.type) {
    case 'text':
      return (
        <Text mt="16px" whiteSpace="pre-line">
          {block.text}
        </Text>
      );
    case 'reasoning':
      return (
        <Text
          mt="16px"
          fontStyle="italic"
          color="gray.500"
          whiteSpace="pre-line"
        >
          {block.summary}
        </Text>
      );
    case 'toolCall':
      return (
        <Text mt="8px" fontSize="14px" color="gray.500">
          🔧 {block.toolName} ({block.status})
        </Text>
      );
    // graphDiff is reserved in the contract but not rendered in step 1.
    default:
      return null;
  }
};

const TurnView = ({ turn }: { turn: AgentVTurn }) => {
  return (
    <Box mb="24px">
      <PromptChatBubble promptString={turn.prompt} />
      {turn.blocks.map((block, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <BlockView key={i} block={block} />
      ))}
    </Box>
  );
};

/**
 * Shared AgentV chat window (step 1). Used by the admin dashboard and the
 * backup-servicing customer portals — they differ only in the `streamUrl`
 * (which finleycore app the SSE route lives on).
 *
 * A dumb consumer of the `AgentV` stream contract: it renders each turn's
 * `AgentVBlock[]` by switching on `block.type`. Only `text` and `reasoning`
 * render today; `toolCall` renders as a minimal status line to prove the tool
 * path works. Richer block renderers (graph / diff) slot into `BlockView`
 * later without touching the transport.
 */
export const AgentVChat = ({
  streamUrl,
  targetOrganizationId,
}: {
  streamUrl: string;
  targetOrganizationId: string;
}) => {
  const { turns, isStreaming, error, model, sendPrompt } = useAgentVStream(
    streamUrl,
    targetOrganizationId,
  );
  const {
    attachments,
    isUploading,
    isLoadingExisting,
    uploadFile,
    loadExistingAttachments,
    downloadAttachment,
    removeAttachment,
  } = useAgentVAttachments();
  const [draft, setDraft] = useState<string>('');

  const onShowPreviousFiles = (): void => {
    loadExistingAttachments().catch(() => {
      // loadExistingAttachments handles its own error toast.
    });
  };

  // Hidden native file input driven by the paperclip button; reset after each
  // pick (via the ref, not the event target) so choosing the same file twice
  // still fires `onChange`.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const onPickFile = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    if (file) {
      uploadFile(file).catch(() => {
        // uploadFile handles its own error toast; swallow to satisfy no-floating.
      });
    }
  };

  // Auto-scroll the transcript to the bottom as new content streams in, but
  // only when the user is already near the bottom. If they've scrolled up to
  // read earlier output, we leave their position alone rather than yanking them
  // back down on every delta.
  const scrollRef = useRef<HTMLDivElement>(null);
  const stickToBottomRef = useRef<boolean>(true);

  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    // Treat "within 80px of the bottom" as still following the stream.
    stickToBottomRef.current = distanceFromBottom < 80;
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el && stickToBottomRef.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns, isStreaming]);

  const onSend = (): void => {
    // Don't clear the draft when there's nothing to send (whitespace-only) —
    // sendPrompt no-ops on it, so clearing would silently drop the user's text.
    if (!draft.trim()) return;
    // Re-lock to the bottom on every send: sending is an explicit intent to see
    // the new turn. Without this, a user who scrolled up (clearing the flag) and
    // then hit Send would stay anchored at the old position while the response
    // streams in off-screen.
    stickToBottomRef.current = true;
    sendPrompt(draft);
    setDraft('');
  };

  return (
    <Flex direction="column" h="70vh" maxW="900px">
      {model && (
        <Text mb="8px" fontSize="12px" color="gray.500">
          Model: {model}
        </Text>
      )}
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        flex="1"
        overflowY="auto"
        pr="8px"
      >
        {turns.map((turn, i) => (
          // Turns are append-only and never reordered, so index is a stable key.
          // eslint-disable-next-line react/no-array-index-key
          <TurnView key={i} turn={turn} />
        ))}
        {isStreaming && (
          <Box mt="16px">
            <FinleyThinkingSpinner />
          </Box>
        )}
        {error && (
          <Text mt="16px" color="red.500">
            {error}
          </Text>
        )}
      </Box>

      {attachments.length > 0 && (
        <Flex mt="12px" gap="8px" wrap="wrap">
          {attachments.map((attachment) => (
            <Tag
              key={attachment.key}
              size="md"
              borderRadius="full"
              variant="subtle"
              gap="6px"
            >
              <Icon icon="paperclip" size="md" />
              <Text
                as="button"
                type="button"
                onClick={() => downloadAttachment(attachment)}
                title={`Download ${attachment.filename}`}
                _hover={{ textDecoration: 'underline' }}
              >
                {attachment.filename}
              </Text>
              <Box
                as="button"
                type="button"
                aria-label={`Remove ${attachment.filename}`}
                onClick={() => removeAttachment(attachment.key)}
                color="gray.500"
              >
                <Icon icon="xmark" size="md" />
              </Box>
            </Tag>
          ))}
        </Flex>
      )}

      <Flex mt="16px" gap="8px">
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={onPickFile}
        />
        <Button
          onClick={() => fileInputRef.current?.click()}
          isLoading={isUploading}
          isDisabled={isStreaming}
          variant="outline"
          aria-label="Attach a file"
          title="Attach a file"
        >
          <Icon icon="paperclip" size="md" />
        </Button>
        <Button
          onClick={onShowPreviousFiles}
          isLoading={isLoadingExisting}
          isDisabled={isStreaming}
          variant="outline"
          aria-label="Show previous files"
          title="Show files you uploaded previously"
        >
          <Icon icon="folder-open" size="md" />
        </Button>
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Ask AgentV…"
          rows={2}
          isDisabled={isStreaming}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
        />
        <Button onClick={onSend} isDisabled={isStreaming || !draft.trim()}>
          Send
        </Button>
      </Flex>
    </Flex>
  );
};
